import { useMemo, useState } from 'react';
import type { ColumnDef, OnChangeFn, VisibilityState } from '@tanstack/react-table';
import type { PreferenceStore } from '@resource-kit/core';
import { useResourceKit } from '@resource-kit/core/react';
import {
  computeDefaultColumnVisibility,
  getToggleableColumnIds,
  columnLabel,
  columnId,
} from './table-export';

export interface ToggleableColumn {
  id: string;
  label: string;
}

export interface ColumnVisibilityHandle {
  columnVisibility: VisibilityState;
  setColumnVisibility: OnChangeFn<VisibilityState>;
  toggleableColumns: ToggleableColumn[];
}

/**
 * Persisted column show/hide state (#1794), lifted out of the table component
 * unchanged (#1801).
 *
 * The store is user-scoped: two operators sharing a machine must not inherit
 * each other's hidden columns, and coming back to your own account must
 * restore yours. `sanitize` below RECOGNISES a flat string→boolean map and
 * returns `null` for anything else; `readVisibility` is the line that acts on
 * that `null`, deleting the entry so a corrupted or format-shifted blob
 * degrades to the column defaults once instead of on every mount forever.
 *
 * `null` rather than `{}` as the rejection value, because `{}` is also a
 * perfectly valid stored map (every column visible) and a caller cannot tell
 * the two apart — which is exactly how the discard went missing in the move.
 *
 * Since #2169 PR-2 the key handed to the seam is LOGICAL — `columns.<base>` —
 * and the host adapter is what prefixes `hgs.`, stamps version 1 and scopes it
 * to the signed-in operator. The physical localStorage key is byte-identical to
 * the one this hook wrote before the move; nothing an operator already set is
 * orphaned.
 */
function sanitize(raw: unknown): VisibilityState | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const entries = Object.entries(raw);
  if (entries.some(([, visible]) => typeof visible !== 'boolean')) return null;
  return Object.fromEntries(entries) as VisibilityState;
}

/**
 * Read the stored map, and DELETE an entry that is present but the wrong shape.
 *
 * Before the move the discard was `lib/store`'s: `createStore` was handed this
 * `sanitize`, and a rejected blob went through `discard()` — `removeItem` plus
 * a `console.error`. The seam moves `unknown` in both directions and
 * deliberately holds no shape opinion, so the discard has to travel here, with
 * the only code that knows what this entry is. `useDataTableParams` took the
 * same step for the other shape on this seam.
 *
 * ONLY THE `removeItem` HALF COMES BACK. `storage.remove` reaches the host
 * adapter's `removeKey(key, true)`, which reports only from its catch, so a
 * SUCCESSFUL discard of a corrupt blob is now silent. That follows from the
 * #2165 seam holding no shape opinion — it cannot report a rejection it has no
 * way to recognise — so the lost `console.error` is a property of the seam, not
 * something this function dropped.
 *
 * Present-but-wrong is CORRUPT; ABSENT is not — and `storage.has` is what
 * tells them apart (#2183). The read cannot: the seam answers `null` both for
 * a key nobody ever wrote and for one holding a stored `null`, which the host
 * store hands back intact because it installs no sanitizer. The null-check on
 * the READ that this replaces answered "absent" for both, so it would leave a
 * stored `null` in place. The point of asking the key is that the consumer no longer has to
 * infer the answer from the value at all: a fresh mount does not turn a read
 * into a write, and an entry that IS there and cannot be used is discarded. A
 * blob the store itself rejects normally never arrives here as present — its
 * own discard removes the key first — unless that removal throws, in which
 * case the discard below is the retry.
 */
function readVisibility(storage: PreferenceStore, storageKey: string): VisibilityState {
  const raw = storage.read(storageKey);
  const parsed = sanitize(raw);
  // Order matters only for cost: `parsed === null` is already in hand, so
  // testing it first spares a redundant `getItem` for a key that HAS a usable
  // value. A key without one — a first-time operator's included — still pays
  // it, and pays for more than one: `store.ts`'s `read` already issued two
  // `getItem` calls on the way to `null` (effective key, then legacy), so
  // `storage.has` here is a third `getItem` on an absent key, not the second.
  if (parsed === null && storage.has(storageKey)) storage.remove(storageKey);
  return parsed ?? {};
}

export function useColumnVisibility<TData, TValue>(
  columns: ColumnDef<TData, TValue>[],
  storageKeyBase: string,
): ColumnVisibilityHandle {
  const { t, storage } = useResourceKit();
  const storageKey = `columns.${storageKeyBase}`;

  const [columnVisibility, setVisibilityState] = useState<VisibilityState>(() => ({
    ...computeDefaultColumnVisibility(columns),
    ...readVisibility(storage, storageKey),
  }));

  const setColumnVisibility: OnChangeFn<VisibilityState> = (updater) => {
    setVisibilityState((previous) => {
      const next = typeof updater === 'function' ? updater(previous) : updater;
      storage.write(storageKey, next);
      return next;
    });
  };

  // Columns the operator may toggle (real data columns; mechanism columns
  // like row actions are excluded). Memoized on the column identity.
  const toggleableColumns = useMemo(() => {
    const ids = new Set(getToggleableColumnIds(columns));
    return columns
      .filter((c) => {
        const id = columnId(c);
        return id != null && ids.has(id);
      })
      .map((c) => ({ id: columnId(c)!, label: columnLabel(c, t) }));
  }, [columns, t]);

  return { columnVisibility, setColumnVisibility, toggleableColumns };
}
